import React, { useMemo, useState } from "react";
import MappingTopBox from "./CreatorTools/MappingTool/components/MappingTopBox";
import { RuleTemplateInputField } from "@rebuild/shared";
import { InputFieldComponent } from "./components/InputFields/InputFieldComponent";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { useFirestoreCollectionReadWrite } from "../hooks/useFirestoreCollectionReadWrite";
import { collection, CollectionReference } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { IDatabaseLineItem, ScopeTag } from "../shared/common/EstimateEngine/EstimateEngineTypes";
import SharedUtils from "../shared/common/SharedUtils";
import { MitToReconMapping, MitToReconMappingAction, MitToReconMappingOverride } from "@rebuild/shared";
import { useLineItemTemplates } from "../hooks/useLineItemTemplates";
import { MitToReconMappingStatus } from "./MitToReconMappingStatus";
import { MitToReconMissingMappings } from "./MitToReconMissingMappings";
import { MIT_TO_RECON_SOURCE_SCOPES } from "./mitToRecon.constants";
import { useAuth } from "../contexts/AuthContext";

const validateMappingActionCombo = (
  mappingAction: MitToReconMappingAction | undefined,
  reconLineItems: { lineItem?: { lineItemId?: string } }[] | undefined,
  dbLineItem: IDatabaseLineItem | undefined,
  context: string,
): string | undefined => {
  if (!mappingAction) {
    return `Please select a mapping action${context}.`;
  }
  if (mappingAction === MitToReconMappingAction.USE_HARDCODED_MAPPING && !reconLineItems?.length) {
    return `Please select at least one recon line item if mapping action is set to 'USE_HARDCODED_MAPPING'${context}.`;
  }
  if (!!reconLineItems?.length && reconLineItems?.some((reconLineItem) => reconLineItem.lineItem?.lineItemId === undefined)) {
    return `Please select a line item for every 'recon line item' object that you have${context}.`;
  }
  if (
    mappingAction === MitToReconMappingAction.KEEP_AS_SAME &&
    !dbLineItem?.scopes?.includes(ScopeTag.interior_reconstruction) &&
    !dbLineItem?.scopes?.includes(ScopeTag.exterior_reconstruction)
  ) {
    return `The mit line item that you selected isn't a valid recon line item, unable to save with mapping action set as KEEP_AS_SAME${context}.`;
  }
  return undefined;
};

const useValidateMapping = (mapping?: Partial<Omit<MitToReconMapping, "id">>): string | undefined => {
  const { dbLineItems } = useLineItemTemplates();

  const dbLineItem = dbLineItems?.find((dbLineItem) => dbLineItem.id === mapping?.mitLineItem?.lineItemId);

  if (mapping === undefined) {
    return "Unable to save undefined mapping object.";
  }

  if (!mapping.mitLineItem?.lineItemId) {
    return "Please select a MIT line item.";
  }
  const defaultError = validateMappingActionCombo(mapping.mappingAction, mapping.reconLineItems, dbLineItem, "");
  if (defaultError) return defaultError;

  for (const [overrideIdx, override] of (mapping.overrides ?? []).entries()) {
    const overrideContext = ` for override #${overrideIdx + 1}`;
    const overrideSelectedValues = (override as MitToReconMappingOverride).mitLineItem?.selectedValues ?? {};
    const hasSelectedValue = Object.values(overrideSelectedValues).some((values) => Array.isArray(values) && values.length > 0);
    if (!hasSelectedValue) {
      return `Please select at least one variable selection${overrideContext}.`;
    }

    const overrideError = validateMappingActionCombo(override.mappingAction, override.reconLineItems, dbLineItem, overrideContext);
    if (overrideError) return overrideError;
  }

  return undefined;
};

const RECON_LINE_ITEMS_FIELD: RuleTemplateInputField = {
  name: "reconLineItems",
  type: "array",
  description: "The recon line item(s) that the MIT line item maps to. Only used when mapping action is USE_HARDCODED_MAPPING.",
  items: [
    {
      name: "lineItem",
      type: "lineItem",
      description: "A target recon line item. Use variable overrides to pin specific variable values on the output.",
      parameters: {
        enableVariableOverride: true,
      },
    },
  ],
};

const MAPPING_ACTION_FIELD: RuleTemplateInputField = {
  name: "mappingAction",
  type: "enum",
  description:
    "How to convert the MIT line item into recon. DROP: remove it. KEEP_AS_SAME: reuse the same line item (only valid if it's already a recon line item). USE_HARDCODED_MAPPING: replace with the recon line items listed below. SEND_TO_LLM: defer mapping to the LLM.",
  values: ["DROP", "KEEP_AS_SAME", "USE_HARDCODED_MAPPING", "SEND_TO_LLM"],
};

const MIT_TO_RECON_MAPPING: RuleTemplateInputField = {
  name: "mitToReconMapping",
  type: "object",
  properties: [
    {
      name: "mitLineItem",
      type: "lineItem",
      description:
        "The MIT line item this mapping applies to. The default mapping action and recon line items below apply to all permutations of this line item unless an override matches.",
      parameters: {
        disablePermutationSelection: true,
      },
    },
    MAPPING_ACTION_FIELD,
    RECON_LINE_ITEMS_FIELD,
    {
      name: "overrides",
      type: "array",
      description:
        "Per-variant overrides of the default mapping. Each override matches a subset of the MIT line item's permutations (via variable selections) and applies its own mapping action.",
      items: [
        {
          name: "mitLineItem",
          type: "lineItem",
          description:
            "Variable selections that pick which permutations of the MIT line item this override applies to. At least one variable must have a selected value.",
          parameters: {
            sourceLineItemPath: "mitToReconMapping.mitLineItem",
            enablePartialSelection: true,
          },
        },
        MAPPING_ACTION_FIELD,
        RECON_LINE_ITEMS_FIELD,
      ],
    },
  ],
};

interface InputFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  inputField: RuleTemplateInputField;
  inputValue: any;
  onUpdate: (updates: any) => void;
  onSave: () => void;
  saveButtonLabel: string;
  showValidationError: boolean;
  validationError: string | undefined;
  lastModifiedBy?: string;
  lastModifiedAt?: number;
}

export const MitToReconPage: React.FC = () => {
  const {
    objects: mappings,
    isLoading,
    saveObject,
    deleteObject,
  } = useFirestoreCollectionReadWrite<MitToReconMapping>("mit_to_recon_mappings");

  const [currentMapping, setCurrentMapping] = useState<Partial<Omit<MitToReconMapping, "id">> | MitToReconMapping | undefined>();
  const [mappingToDelete, setMappingToDelete] = useState<MitToReconMapping | undefined>();
  const [showValidationError, setShowValidationError] = useState(false);
  const { currentUser } = useAuth();

  const { dbLineItems, isLoading: isLoadingDbLineItems } = useLineItemTemplates();
  const dbLineItemsById = useMemo(() => new Map(dbLineItems.map((dbLineItem) => [dbLineItem.id, dbLineItem])), [dbLineItems]);

  const validationError = useValidateMapping(currentMapping);

  const onSave = async () => {
    try {
      if (currentMapping === undefined) {
        throw new Error("Attempting to save a mapping that is undefined");
      }

      if (validationError) {
        setShowValidationError(true);
        return;
      }

      const obj = {
        ...("id" in currentMapping ? currentMapping : { id: uuidv4(), ...currentMapping }),
        lastModifiedBy: currentUser?.email ?? currentUser?.uid,
        lastModifiedAt: Date.now(),
      };

      await saveObject((obj as MitToReconMapping).id, obj as MitToReconMapping);
    } catch (e) {
      toast.error("Encountered error when saving mapping");
    }
    setCurrentMapping(undefined);
  };

  const openModalForCreate = (mitLineItemId?: string) => {
    setShowValidationError(false);
    setCurrentMapping(mitLineItemId ? { mitLineItem: { lineItemId: mitLineItemId } } : {});
  };

  const openModalForEdit = (mapping: MitToReconMapping) => {
    setShowValidationError(false);
    setCurrentMapping(mapping);
  };

  const onDelete = async () => {
    if (!mappingToDelete) return;
    try {
      await deleteObject(mappingToDelete.id);
    } catch (e) {
      toast.error("Encountered error when deleting mapping");
    }
    setMappingToDelete(undefined);
  };

  if (!mappings?.length && isLoading) {
    return <>Loading...</>;
  }

  const isModalOpen = currentMapping !== undefined;
  const isEditing = isModalOpen && "id" in currentMapping;

  return (
    <div className="min-h-screen bg-gray-50">
      <MappingTopBox />
      <div className="ml-8 mr-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 mt-4">MIT To Recon</h1>

        <MitToReconMappingStatus mappings={mappings} />
        <MitToReconMissingMappings mappings={mappings} onSelectLineItem={openModalForCreate} />

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Mappings</h2>
            <button
              onClick={() => openModalForCreate()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Mapping
            </button>
          </div>

          <div className="space-y-3">
            {mappings.map((mapping) => (
              <MappingRow
                key={mapping.id}
                mapping={mapping}
                dbLineItem={mapping.mitLineItem?.lineItemId ? dbLineItemsById.get(mapping.mitLineItem.lineItemId) : undefined}
                isLoadingDbLineItem={isLoadingDbLineItems}
                onEdit={openModalForEdit}
                onDelete={setMappingToDelete}
              />
            ))}
            {mappings.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500">No mappings added yet.</p>
              </div>
            )}
          </div>
        </div>

        <InputFieldModal
          isOpen={isModalOpen}
          onClose={() => setCurrentMapping(undefined)}
          title={isEditing ? "Edit Mapping" : "Add Mapping"}
          inputField={MIT_TO_RECON_MAPPING}
          inputValue={{ [MIT_TO_RECON_MAPPING.name]: currentMapping }}
          onUpdate={(updates) => setCurrentMapping(updates[MIT_TO_RECON_MAPPING.name])}
          onSave={onSave}
          saveButtonLabel={isEditing ? "Update Mapping" : "Save Mapping"}
          showValidationError={showValidationError}
          validationError={validationError}
          lastModifiedBy={currentMapping?.lastModifiedBy}
          lastModifiedAt={currentMapping?.lastModifiedAt}
        />

        <ConfirmDeleteModal isOpen={!!mappingToDelete} onClose={() => setMappingToDelete(undefined)} onConfirm={onDelete} />
      </div>
    </div>
  );
};

const InputFieldModal: React.FC<InputFieldModalProps> = ({
  isOpen,
  onClose,
  title,
  inputField,
  inputValue,
  onUpdate,
  onSave,
  saveButtonLabel,
  showValidationError,
  validationError,
  lastModifiedBy,
  lastModifiedAt,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[1600px] max-w-[95vw] max-h-[90vh] flex flex-col relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl z-10">
          ×
        </button>
        <div className="p-6 overflow-y-auto flex-1">
          <h2 className={`text-xl font-bold text-gray-800 ${lastModifiedBy || lastModifiedAt ? "mb-1" : "mb-6"}`}>{title}</h2>
          {(lastModifiedBy || lastModifiedAt) && (
            <p className="text-sm text-gray-500 mb-6">
              Last modified
              {lastModifiedBy && (
                <>
                  {" by "}
                  <span className="font-medium text-gray-700">{lastModifiedBy}</span>
                </>
              )}
              {lastModifiedAt && ` on ${new Date(lastModifiedAt).toLocaleString()}`}
            </p>
          )}
          <div className="space-y-4">
            <InputFieldComponent
              objectInput={inputValue}
              inputField={inputField}
              inputValue={inputValue}
              path={inputField.name}
              onUpdate={onUpdate}
            />
          </div>
          <div className="mt-6">
            {showValidationError && validationError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{validationError}</div>
            )}
            <button
              onClick={onSave}
              disabled={showValidationError && !!validationError}
              className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {saveButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmDeleteModal: React.FC<{ isOpen: boolean; onClose: () => void; onConfirm: () => void }> = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-96 relative">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Mapping</h2>
          <p className="text-gray-600 mb-6">Are you sure you want to delete this mapping? This action cannot be undone.</p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MappingRow: React.FC<{
  mapping: MitToReconMapping;
  dbLineItem: IDatabaseLineItem | undefined;
  isLoadingDbLineItem: boolean;
  onEdit: (mapping: MitToReconMapping) => void;
  onDelete: (mapping: MitToReconMapping) => void;
}> = ({ mapping, dbLineItem, isLoadingDbLineItem, onEdit, onDelete }) => {
  const getDisplayName = (): string => {
    if (!mapping.mitLineItem?.lineItemId) return "No line item";
    if (isLoadingDbLineItem) return "Loading...";
    return dbLineItem?.originalLineItem ?? mapping.mitLineItem.lineItemId;
  };

  const sourceScopes = MIT_TO_RECON_SOURCE_SCOPES.filter(({ scope }) => dbLineItem?.scopes?.includes(scope));

  return (
    <div className="border p-4 rounded-lg flex justify-between items-center hover:bg-gray-50">
      <div className="flex items-center gap-4">
        <span className="font-medium text-gray-800">{getDisplayName()}</span>
        {sourceScopes.map(({ scope, label, tagClassName }) => (
          <span key={scope} className={`text-xs px-2 py-1 rounded-full ${tagClassName}`}>
            {label}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1">
        {mapping.mappingAction && (
          <span className="text-xs px-2 py-1 mr-2 rounded-full bg-gray-100 text-gray-800">{mapping.mappingAction}</span>
        )}
        <button onClick={() => onEdit(mapping)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        <button
          onClick={() => onDelete(mapping)}
          className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
          title="Delete"
        >
          <img src="/assets/ui/trash-01.svg" alt="Delete" className="w-4 h-4 filter brightness-0 invert" />
        </button>
      </div>
    </div>
  );
};

export default MitToReconPage;
